import * as React from "react";

// MUI
import {
	withStyles,
	WithStyles,
	createStyles,
	Theme,
} from "@material-ui/core/styles";
import { Grid } from "@material-ui/core";

// NPM
import { animated, useSpring } from "react-spring";
import { Waypoint } from "react-waypoint";

// Utility
import Utility from "../domain/classes/Utility";

// Components
import StyledSectionContainer from "../components/common/SectionContainer.Component";
import { AnimatedPrimaryInfoFieldsContainer } from "../components/personDetailsInput/PrimaryInfoFieldsContainer.Component";
import { AnimatedContactDetailsFieldsContainerV2 } from "../components/personDetailsInput/ContactDetailsFieldsContainerV2.Component";
import { AnimatedStudentStatusFieldsContainer } from "../components/personDetailsInput/StudentStatusFieldsContainer.Component";
import { AnimatedAttendancePreferenceFieldsContainer } from "../components/personDetailsInput/AttendancePreferenceFieldContainer.Component";
import { AnimatedDateOfBirthFieldsContainerV2 } from "../components/personDetailsInput/DateOfBirthFieldsContainerV2.Component";
import AppointmentDetails from "../components/common/AppointmentDetails.Component";
import LoadingButton from "../components/common/LoadingButton.Component";

// Contexts
import { useApplicationManager } from "../contexts/ApplicationManager.Context";

// Classes
import Patient from "../domain/classes/Patient";
import Journey from "../domain/classes/Journey";

// Errors
import IncompleteFormError from "../domain/classes/errors/IncompleteFormError";
import { ROOT_ID } from "../domain/utility/config";
import { AnimatedTelehealthCheckboxContainer } from "../components/personDetailsInput/TelehealthCheckboxContainer.Component";

export const styles = (theme: Theme) => createStyles({ root: {} });

export interface PersonalDetailsInputSectionV2Props {}

const PersonalDetailsInputSectionV2 = React.forwardRef<
	HTMLDivElement,
	PersonalDetailsInputSectionV2Props & WithStyles<typeof styles>
>(({ classes }, ref) => {
	const {
		patientDetails,
		handlePatientValidationErrorChange,
		patientValidationError,
		journeyStage,
		handleJourneyStageChange,
		handleJourneyErrorChange,
	} = useApplicationManager();

	// States
	const [setToFixed, setSetToFixed] = React.useState<boolean>(false);
	const [loading, setLoading] = React.useState<boolean>(false);
	const [
		isTelehealthRestrictionAcknowledged,
		setIsTelehealthResitrctionAcknowledged,
	] = React.useState<boolean>(false);

	const handleNextButtonClick = () => {
		setLoading(true);
		const { error } = Patient.schema.validate(patientDetails);
		try {
			if (error) {
				const errorParts: string[] = error.message.split("|");
				const errorPath: string = errorParts[0];
				const errorMessage: string = errorParts[1];

				handlePatientValidationErrorChange({
					...patientValidationError,
					[errorPath]: errorMessage,
				});
				throw new IncompleteFormError();
			}
			handleJourneyStageChange(Journey.goFrom(journeyStage, "forward"));
		} catch (error) {
			handleJourneyErrorChange(error.message);
		}
		Utility.scrollToTopOfElement(ROOT_ID);
		setLoading(false);
	};

	const handleTelehealthRestrictionAcknowledgedChange = (value: boolean) => {
		setIsTelehealthResitrctionAcknowledged(value);
	};

	const isButtonDisabled = () => {
		if (
			patientDetails.isTelehealth &&
			patientDetails.attendancePreference === "telehealth" &&
			!isTelehealthRestrictionAcknowledged
		)
			return true;
		return false;
	};

	// Animation springs
	const spring = useSpring({
		position: "relative",
		backgroundColor: "white",
		zIndex: 200,
		paddingBottom: setToFixed ? 0 : 8,
	});

	React.useEffect(() => {
		if (
			!(
				patientDetails.isTelehealth &&
				patientDetails.attendancePreference === "telehealth"
			)
		) {
			setIsTelehealthResitrctionAcknowledged(false);
		}
	}, [patientDetails.isTelehealth, patientDetails.attendancePreference]);

	return (
		<StyledSectionContainer
			ref={ref}
			className={classes.root}
			maxWidth="sm"
		>
			<animated.div
				style={{ ...spring, width: setToFixed ? "100%" : "auto" }}
			>
				<AppointmentDetails />
			</animated.div>

			<Waypoint
				onLeave={() => setSetToFixed(true)}
				onEnter={() => setSetToFixed(false)}
			></Waypoint>

			<AnimatedPrimaryInfoFieldsContainer />
			<AnimatedDateOfBirthFieldsContainerV2 />
			<AnimatedContactDetailsFieldsContainerV2 />
			<AnimatedStudentStatusFieldsContainer />
			<AnimatedAttendancePreferenceFieldsContainer />
			{patientDetails.isTelehealth &&
				patientDetails.attendancePreference === "telehealth" && (
					<AnimatedTelehealthCheckboxContainer
						isTelehealthRestrictionAcknowledged={
							isTelehealthRestrictionAcknowledged
						}
						onTelehealthRestrictionAcknowledgedChange={
							handleTelehealthRestrictionAcknowledgedChange
						}
					/>
				)}

			<Grid container spacing={2} style={{ marginTop: 8 }}>
				<Grid item xs={12}>
					<LoadingButton
						fullWidth
						onClick={handleNextButtonClick}
						isLoading={loading}
						disabled={isButtonDisabled()}
					>
						Next
					</LoadingButton>
				</Grid>
			</Grid>
		</StyledSectionContainer>
	);
});

const StyledPersonalDetailsInputSectionV2 = withStyles(styles, {
	classNamePrefix: Utility.generateClassName("PersonalDetailsInputSectionV2"),
})(PersonalDetailsInputSectionV2);

export default StyledPersonalDetailsInputSectionV2;

export const AnimatedPersonalDetailsInputSectionV2 = animated(
	StyledPersonalDetailsInputSectionV2
);
